import api from '@/utils/axios';
import {
    resolveDashboard,
} from '@/shared/types/users.types';

function getToken(): string | null {
    return localStorage.getItem('auth_token');
}

export function authHeaders(): Record<string, string> {
    const token = getToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

export interface AuthType {
    id: number;
    name: string;
}

export interface AuthUser {
    id: number;
    firstnames: string;
    lastname: string;
    email: string | null;
    email_verified?: boolean;
    email_verified_at?: string | null;
    phone: string;
    status: string;
    zone?: { id: number; name: string } | null;
    types?: AuthType[];
    permissions?: { id: number; name: string }[];
}

export interface LoginResponse {
    token: string;
    token_type: string;
    user: AuthUser;
}

export interface RegisterApiResponse {
    user: AuthUser;
    type: AuthType;
    documents: unknown[];
    token: string;
}

export interface SessionData {
    token: string;
    roleCode: string;
    user: AuthUser;
    dashboardPath: string;
}

export function getPrimaryRoleCode(user: AuthUser): string {
    return user.types?.[0]?.name ?? '';
}

export function saveSession(response: LoginResponse | RegisterApiResponse): SessionData {
    const token = response.token ?? '';
    const user = response.user;
    const roleCode = getPrimaryRoleCode(user);
    const dashboardPath = resolveDashboard(roleCode);

    if (token) {
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_role', roleCode);
        localStorage.setItem('user', JSON.stringify(user));
    }

    return { token, roleCode, user, dashboardPath };
}

export function getSession(): SessionData | null {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    const roleCode = localStorage.getItem('user_role') ?? '';
    const raw = localStorage.getItem('user');
    const user: AuthUser = raw ? JSON.parse(raw) : ({} as AuthUser);

    return {
        token,
        roleCode,
        user,
        dashboardPath: resolveDashboard(roleCode),
    };
}

export function isAuthenticated(): boolean {
    return !!getToken();
}

export function clearSession(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user');
    localStorage.removeItem('user_entity_type');
}

export async function register(payload: Record<string, unknown>): Promise<RegisterApiResponse> {
    // Check if payload contains files (documents)
    const hasDocuments = payload.documents && Array.isArray(payload.documents) && payload.documents.length > 0;

    if (hasDocuments) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Add all non-file fields
        Object.entries(payload).forEach(([key, value]) => {
            if (key === 'documents') return;

            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                    // Handle arrays with bracket notation
                    value.forEach((item) => {
                        formData.append(`${key}[]`, String(item));
                    });
                } else if (typeof value === 'object' && !(value instanceof File)) {
                    // Handle nested objects with bracket notation
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        formData.append(`${key}[${subKey}]`, String(subValue));
                    });
                } else {
                    formData.append(key, String(value));
                }
            }
        });

        // Add documents as files
        if (Array.isArray(payload.documents)) {
            payload.documents.forEach((doc: any, index: number) => {
                if (doc.file) {
                    formData.append(`documents[${index}][category_id]`, String(doc.category_id));
                    formData.append(`documents[${index}][file]`, doc.file);
                    formData.append(`documents[${index}][started_at]`, doc.started_at);
                    formData.append(`documents[${index}][ended_at]`, doc.ended_at);
                }
            });
        }

        const res = await api.post('/register', formData, {
            headers: {
                'Content-Type': undefined,
            },
        });
        return res.data;
    } else {
        // Use JSON for regular registration without documents
        const res = await api.post('/register', payload);
        return res.data;
    }
}

export async function joinEntity(
    token: string,
    payload: Record<string, unknown>,
): Promise<RegisterApiResponse> {
    const res = await api.post(`/join/${token}`, payload);
    return res.data;
}

export async function login(identifier: string, password: string): Promise<LoginResponse> {
    const isEmail = /\S+@\S+\.\S+/.test(identifier);
    const body: Record<string, string> = { password };

    if (isEmail) {
        body.email = identifier;
    } else {
        body.phone = identifier.replace(/[\s\-(). ]/g, '');
    }

    const res = await api.post('/login', body);
    return res.data;
}

export async function logout(): Promise<void> {
    try {
        await api.post('/logout');
    } catch (e) {
        console.error('Erreur lors de la déconnexion côté serveur', e);
    } finally {
        clearSession();
    }
}

export async function getMe(): Promise<AuthUser> {
    const res = await api.get('/me');
    return res.data.data ?? res.data;
}

export interface ForgotPasswordResponse {
    message: string;
    channel?: string | null;
    debug_code?: string;
    debug_login?: string;
}

export async function forgotPassword(login: string): Promise<ForgotPasswordResponse> {
    const res = await api.post('/forgot-password', { login });
    return res.data;
}

export async function resetPassword(payload: {
    login: string;
    token: string;
    password: string;
    password_confirmation: string;
}): Promise<{ message: string }> {
    const res = await api.post('/reset-password', payload);
    return res.data;
}

export interface EmailOtpSendResponse {
    message: string;
    expires_in: number;
    debug_otp_code?: string;
}

export interface EmailOtpVerifyResponse {
    message: string;
    email_verification_token: string;
}

export async function sendEmailOtp(
    email: string,
    purpose: 'registration' | 'account' = 'registration',
): Promise<EmailOtpSendResponse> {
    const res = await api.post('/email-otp/send', { email, purpose });
    return res.data;
}

export async function verifyEmailOtp(
    email: string,
    code: string,
): Promise<EmailOtpVerifyResponse> {
    const res = await api.post('/email-otp/verify', { email, code });
    return res.data;
}

export async function resendEmailOtp(): Promise<EmailOtpSendResponse> {
    const res = await api.post('/email-otp/resend');
    return res.data;
}

export async function confirmEmailOtp(code: string): Promise<{ message: string }> {
    const res = await api.post('/email-otp/confirm', { code });
    return res.data;
}
